import { Facebook, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "@/lib/site";

function GoogleG({ className = "w-4 h-4" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20.4H24v7.2h11.2c-1.5 4.2-5.5 7.2-11.2 7.2-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.1-5.1C33.9 6 29.2 4 24 4 13 4 4 13 4 24s9 20 20 20c11 0 20-9 20-20 0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l5.9 4.3C13.9 15 18.6 12 24 12c3 0 5.8 1.1 7.9 3l5.1-5.1C33.9 6 29.2 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.1 0 9.8-1.9 13.3-5.1l-6.1-5c-2 1.4-4.5 2.2-7.2 2.2-5.6 0-10.3-3-11.9-7.1l-6 4.6C9.5 39.5 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20.4H24v7.2h11.2c-.7 2-2 3.7-3.7 5l6.1 5C41.1 34.6 44 29.6 44 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="bg-[#101010] border-t border-[#2E3036]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3">
            <span className="relative inline-flex h-10 w-10 items-center justify-center border border-[#D4A84B]">
              <span className="absolute inset-1 border border-[#D4A84B]/40" />
              <span className="font-serif-display text-[#D4A84B] text-xl leading-none">E</span>
            </span>
            <div className="leading-none">
              <p className="font-serif-display text-white text-xl">Everstone Roofing</p>
              <p className="es-label text-[#D4A84B] mt-1">Milwaukee · Wisconsin</p>
            </div>
          </div>
          <p className="text-white/55 text-sm mt-6 max-w-sm leading-relaxed">
            Local exterior contractor serving greater Milwaukee. Roofing, gutters,
            siding, soffit, fascia, and exterior storm restoration. GAF Certified
            Residential Roofing Contractor.
          </p>

          {/* Social icons */}
          <div className="mt-6 flex items-center gap-3" aria-label="Social media">
            <a
              href={SITE.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="footer-social-facebook"
              className="inline-flex items-center justify-center w-9 h-9 border border-[#2E3036] text-white/70 hover:text-[#D4A84B] hover:border-[#D4A84B] transition-colors"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href={SITE.social.google}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Google Business Profile"
              data-testid="footer-social-google"
              className="inline-flex items-center justify-center w-9 h-9 border border-[#2E3036] hover:border-[#D4A84B] transition-colors bg-white/5"
            >
              <GoogleG className="w-4 h-4" />
            </a>
            <a
              href={SITE.social.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              data-testid="footer-social-linkedin"
              className="inline-flex items-center justify-center w-9 h-9 border border-[#2E3036] text-white/70 hover:text-[#D4A84B] hover:border-[#D4A84B] transition-colors"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="es-label text-white/50 mb-4">Services</p>
          <ul className="space-y-3 text-white/75 text-sm">
            <li>Roofing</li>
            <li>Storm & Hail Damage</li>
            <li>Gutters & Downspouts</li>
            <li>Siding</li>
            <li>Soffit & Fascia</li>
            <li className="text-white/45">Windows · on request</li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="es-label text-white/50 mb-4">Get In Touch</p>
          <ul className="space-y-3 text-white/75 text-sm">
            <li>
              <a href={`tel:${SITE.phone.replace(/[^0-9]/g, "")}`} className="hover:text-[#D4A84B] transition-colors">
                {SITE.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${SITE.email}`} className="hover:text-[#D4A84B] transition-colors">
                {SITE.email}
              </a>
            </li>
            <li>Franklin, WI 53132 · Serving Southeast Wisconsin</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#2E3036]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-white/45 text-xs">
          <p>© {year} Everstone Roofing Inc. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="hover:text-[#D4A84B] transition-colors"
            >
              Privacy Policy
            </Link>
            <p>Built in Milwaukee, Wisconsin.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
